import * as React from 'react';
import { ReportChainNode } from './types';
import { reportStatusName } from './config';

interface IProps {
  nodes: ReportChainNode[];
  selectedReportId: number | null;
  onSelectNode: (reportId: number) => void;
}

interface IEdge {
  from: number;
  to: number;
  kind: 'M' | 'S';
}

interface ILaidOutNode {
  node: ReportChainNode;
  x: number;
  y: number;
  level: number;
}

const NODE_WIDTH = 170;
const NODE_HEIGHT = 110;
const H_GAP = 90;
const V_GAP = 34;
const MARGIN = 40;

// Role indicator badges shown on nodes.
const ROLE_COLORS = {
  original: '#0d6efd',
  terminal: '#212529',
  completed: '#198754',
  deleted: '#dc3545',
};

const COMPLETED_STATUS_ID = 3;
const DELETED_STATUS_ID = 6;

// Colors keyed to backend eReportStatus ids.
const STATUS_COLORS: { [id: number]: string } = {
  1: '#6c757d', // New
  2: '#0d6efd', // Active
  3: '#198754', // Completed
  4: '#fd7e14', // Merged
  5: '#6610f2', // Management
  6: '#dc3545', // Deleted
  7: '#20c997', // Supplemented
};

const statusColor = (id: number | null | undefined): string =>
  id != null && STATUS_COLORS[id] ? STATUS_COLORS[id] : '#adb5bd';

/**
 * Interactive, dependency-free SVG visualization of a report chain (family).
 * Nodes are laid out left-to-right by their distance from a root (a report that
 * nothing else points to). Merge/supplement pointers are drawn as directed edges.
 * Clicking a node selects it for editing in the parent controller.
 */
const ReportChainGraph: React.FC<IProps> = ({ nodes, selectedReportId, onSelectNode }) => {
  const idSet = new Set(nodes.map((n) => n.reportId));

  // The Original report is the single report whose CrashKey equals the
  // OriginalCrashKey (both non-empty). If more than one qualifies, mark only
  // the lowest report id so exactly one node carries the Original badge.
  const originalReportId: number | null = nodes
    .filter(
      (n) =>
        !!n.crashKey &&
        !!n.originalCrashKey &&
        n.crashKey === n.originalCrashKey
    )
    .map((n) => n.reportId)
    .sort((a, b) => a - b)[0] ?? null;

  // Build directed edges (child -> the report it points to). Only keep edges
  // whose target is part of the loaded family so the drawing stays consistent.
  const edges: IEdge[] = [];
  nodes.forEach((n) => {
    if (n.mergedToReportId != null && idSet.has(n.mergedToReportId)) {
      edges.push({ from: n.reportId, to: n.mergedToReportId, kind: 'M' });
    }
    if (n.supplementedByReportId != null && idSet.has(n.supplementedByReportId)) {
      edges.push({ from: n.reportId, to: n.supplementedByReportId, kind: 'S' });
    }
  });

  // Longest-path layering from roots (nodes with no incoming edges).
  const incoming: { [id: number]: number } = {};
  const outgoing: { [id: number]: number[] } = {};
  nodes.forEach((n) => {
    incoming[n.reportId] = 0;
    outgoing[n.reportId] = [];
  });
  edges.forEach((e) => {
    outgoing[e.from].push(e.to);
    incoming[e.to] = (incoming[e.to] || 0) + 1;
  });

  const level: { [id: number]: number } = {};
  const assignLevel = (id: number, depth: number, guard: Set<number>) => {
    if (guard.has(id)) {
      return; // cycle guard
    }
    guard.add(id);
    if (level[id] === undefined || depth > level[id]) {
      level[id] = depth;
    }
    outgoing[id].forEach((t) => assignLevel(t, depth + 1, guard));
    guard.delete(id);
  };

  nodes
    .filter((n) => (incoming[n.reportId] || 0) === 0)
    .forEach((n) => assignLevel(n.reportId, 0, new Set<number>()));

  // Any node not reached (e.g. inside a pure cycle) defaults to level 0.
  nodes.forEach((n) => {
    if (level[n.reportId] === undefined) {
      level[n.reportId] = 0;
    }
  });

  // Always pin the Original report to the top-left: give it level 0 (leftmost
  // column) so it renders at the far left, and it is sorted first (top row)
  // within its column below.
  if (originalReportId != null) {
    level[originalReportId] = 0;
  }

  // Group by level and stack vertically.
  const byLevel: { [lvl: number]: ReportChainNode[] } = {};
  nodes.forEach((n) => {
    const lvl = level[n.reportId];
    if (!byLevel[lvl]) {
      byLevel[lvl] = [];
    }
    byLevel[lvl].push(n);
  });

  const laidOut: { [id: number]: ILaidOutNode } = {};
  let maxRows = 0;
  Object.keys(byLevel)
    .map((k) => parseInt(k, 10))
    .sort((a, b) => a - b)
    .forEach((lvl) => {
      // The Original report always sorts first (top) within its column.
      const group = byLevel[lvl].sort((a, b) => {
        if (a.reportId === originalReportId) return -1;
        if (b.reportId === originalReportId) return 1;
        return a.reportId - b.reportId;
      });
      maxRows = Math.max(maxRows, group.length);
      group.forEach((n, row) => {
        laidOut[n.reportId] = {
          node: n,
          level: lvl,
          x: MARGIN + lvl * (NODE_WIDTH + H_GAP),
          y: MARGIN + row * (NODE_HEIGHT + V_GAP),
        };
      });
    });

  const levelCount = Object.keys(byLevel).length;
  const svgWidth = MARGIN * 2 + levelCount * NODE_WIDTH + Math.max(0, levelCount - 1) * H_GAP;
  const svgHeight = MARGIN * 2 + maxRows * NODE_HEIGHT + Math.max(0, maxRows - 1) * V_GAP;

  const renderEdge = (edge: IEdge, index: number) => {
    const from = laidOut[edge.from];
    const to = laidOut[edge.to];
    if (!from || !to) {
      return null;
    }
    const startX = from.x + NODE_WIDTH;
    const startY = from.y + NODE_HEIGHT / 2;
    const endX = to.x;
    const endY = to.y + NODE_HEIGHT / 2;
    const midX = (startX + endX) / 2;
    const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
    const color = edge.kind === 'M' ? '#fd7e14' : '#20c997';
    return (
      <g key={`edge-${index}`}>
        <path d={path} stroke={color} strokeWidth={2} fill="none" markerEnd="url(#arrow)" />
        <text x={midX} y={(startY + endY) / 2 - 4} fontSize={11} fill={color} textAnchor="middle">
          {edge.kind === 'M' ? 'Merged' : 'Supplemented'}
        </text>
      </g>
    );
  };

  const renderNode = (item: ILaidOutNode) => {
    const { node, x, y } = item;
    const isSelected = node.reportId === selectedReportId;
    const color = statusColor(node.reportStatusId);

    // Compute role indicators for this node within the loaded family.
    // Exactly one node is the Original (see originalReportId above).
    const isOriginal = node.reportId === originalReportId;
    const isTerminal = (outgoing[node.reportId] || []).length === 0;
    const isCompleted = node.reportStatusId === COMPLETED_STATUS_ID;
    const isDeleted = node.reportStatusId === DELETED_STATUS_ID;

    const badges: { label: string; color: string }[] = [];
    if (isOriginal) badges.push({ label: 'Original', color: ROLE_COLORS.original });
    if (isTerminal) badges.push({ label: 'Terminal', color: ROLE_COLORS.terminal });
    if (isCompleted) badges.push({ label: 'Completed', color: ROLE_COLORS.completed });
    if (isDeleted) badges.push({ label: 'Deleted', color: ROLE_COLORS.deleted });

    let badgeX = 8;
    const badgeY = 90;
    const renderBadges = badges.map((b, i) => {
      const badgeWidth = b.label.length * 6 + 10;
      const bx = badgeX;
      badgeX += badgeWidth + 4;
      return (
        <g key={`badge-${node.reportId}-${i}`}>
          <rect x={bx} y={badgeY} width={badgeWidth} height={14} rx={7} ry={7} fill={b.color} />
          <text x={bx + badgeWidth / 2} y={badgeY + 10} fontSize={9} fill="#ffffff" textAnchor="middle">
            {b.label}
          </text>
        </g>
      );
    });

    return (
      <g
        key={`node-${node.reportId}`}
        transform={`translate(${x}, ${y})`}
        style={{ cursor: 'pointer' }}
        onClick={() => onSelectNode(node.reportId)}
      >
        <rect
          width={NODE_WIDTH}
          height={NODE_HEIGHT}
          rx={6}
          ry={6}
          fill="#ffffff"
          stroke={isSelected ? '#000000' : color}
          strokeWidth={isSelected ? 3 : 1.5}
        />
        <rect width={NODE_WIDTH} height={20} rx={6} ry={6} fill={color} />
        <rect y={12} width={NODE_WIDTH} height={8} fill={color} />
        <text x={8} y={14} fontSize={11} fontWeight="bold" fill="#ffffff">
          Report {node.reportId}
        </text>
        <text x={8} y={38} fontSize={11} fill="#212529">
          {node.reportTypeName || 'Report'}
        </text>
        <text x={8} y={54} fontSize={11} fill="#212529">
          {(() => {
            const statusText =
              reportStatusName(node.reportStatusId != null ? node.reportStatusId : null) ||
              '(no status)';
            if (node.reportStatusId === DELETED_STATUS_ID) {
              return statusText;
            }
            if (node.supplementedByReportId != null) {
              return `${statusText} \u2192 ${node.supplementedByReportId}`;
            }
            if (node.mergedToReportId != null) {
              return `${statusText} \u2192 ${node.mergedToReportId}`;
            }
            return statusText;
          })()}
        </text>
        <text x={8} y={68} fontSize={10} fill="#0d6efd">
          Queue: {node.currentWorkflowStep || 'n/a'}
        </text>
        <text x={8} y={82} fontSize={10} fill="#6c757d">
          Doc: {node.documentId || 'n/a'}
        </text>
        {renderBadges}
      </g>
    );
  };

  return (
    <div style={{ overflow: 'auto', border: '1px solid #dee2e6', borderRadius: 4, background: '#f8f9fa' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          padding: '6px 10px',
          borderBottom: '1px solid #dee2e6',
          fontSize: 11,
        }}
      >
        {[
          { label: 'Original', color: ROLE_COLORS.original },
          { label: 'Terminal', color: ROLE_COLORS.terminal },
          { label: 'Completed', color: ROLE_COLORS.completed },
          { label: 'Deleted', color: ROLE_COLORS.deleted },
        ].map((l) => (
          <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 5,
                background: l.color,
              }}
            />
            {l.label}
          </span>
        ))}
      </div>
      <svg width={svgWidth} height={svgHeight} style={{ display: 'block' }}>
        <defs>
          <marker
            id="arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#495057" />
          </marker>
        </defs>
        {edges.map(renderEdge)}
        {Object.keys(laidOut).map((id) => renderNode(laidOut[parseInt(id, 10)]))}
      </svg>
    </div>
  );
};

export default ReportChainGraph;
